import { useState, useEffect } from 'react';
import { Info, Server, Globe, ArrowRightLeft, ChevronRight, ChevronDown, ExternalLink, Network } from 'lucide-react';
import { getSubscriptionTypeBadge } from '../../../utils/subscriptionUtils';
import edgeService from '../../../services/edge.service';
import { useTenantStore } from '../../../stores/tenantStore';

interface SubscriptionsProps {
  edgeId: string;
  /**
   * Invoked when the user clicks "Edit in Logical Interfaces" on a group.
   * The parent EdgeConfig page switches to the Logical Interfaces tab so
   * the user can open the owning interface modal and manage subscriptions
   * there.
   */
  onNavigateToLogicalInterfaces?: () => void;
}

type TypeFilter = 'all' | 'dhcp_pool' | 'nat';

/**
 * Read-only cross-interface summary of all subscriptions on this edge.
 *
 * Subscription management now lives inside the Logical Interface modal — each
 * interface owns its DHCP pools (LAN), Source NAT (WAN), and Port Forwarding
 * (WAN) rules in-context. This page is a summary and navigation aid: users
 * see everything at a glance, then open the relevant interface from the
 * Logical Interfaces tab to make changes.
 */
const Subscriptions = ({ edgeId, onNavigateToLogicalInterfaces }: SubscriptionsProps) => {
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [activeFilter, setActiveFilter] = useState<TypeFilter>('all');
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setLoading(true);
        const response = await edgeService.listSubscriptions(edgeId);
        setSubscriptions(response.items || []);
      } catch (err) {
        console.error('Failed to load subscriptions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSubscriptions();
  }, [edgeId, selectedTenantId]);

  const filteredSubscriptions = subscriptions.filter((sub) => {
    const subType = sub.subscription_type || sub.type;
    if (activeFilter === 'all') return true;
    if (activeFilter === 'nat') return subType === 'source_nat' || subType === 'port_forwarding';
    return subType === activeFilter;
  });

  // Group by logical interface name (pulled from the row by the API when it
  // joins dev_logical_port). Falls back to the raw logical_port_id if the
  // name wasn't attached — shouldn't happen in practice.
  const groups = filteredSubscriptions.reduce<Record<string, any[]>>((acc, sub) => {
    const ifaceName = sub.logical_interface_name
      || sub.logicalInterface
      || sub.logical_port_id
      || 'Unassigned';
    (acc[ifaceName] ||= []).push(sub);
    return acc;
  }, {});

  const groupNames = Object.keys(groups).sort((a, b) => a.localeCompare(b));
  const toggleGroup = (name: string) =>
    setCollapsedGroups((p) => ({ ...p, [name]: !p[name] }));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Subscriptions</h3>
        <p className="text-sm text-gray-600 mt-1">
          Read-only summary of DHCP pools, NAT rules, and port forwards across this edge.
        </p>
      </div>

      {/* Navigation hint */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Subscriptions are configured per-interface.
          Open the <strong>Logical Interfaces</strong> tab and edit an interface to add,
          change, or remove its DHCP pools, Source NAT, or Port Forwarding rules.
        </p>
      </div>

      {/* Filter chips */}
      <div className="flex items-center space-x-2">
        {([
          { id: 'all' as TypeFilter, label: 'All', count: subscriptions.length },
          {
            id: 'dhcp_pool' as TypeFilter,
            label: 'DHCP Pools',
            count: subscriptions.filter((s) => (s.subscription_type || s.type) === 'dhcp_pool').length,
          },
          {
            id: 'nat' as TypeFilter,
            label: 'NAT / Port Forwarding',
            count: subscriptions.filter((s) =>
              ['source_nat', 'port_forwarding'].includes(s.subscription_type || s.type),
            ).length,
          },
        ]).map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveFilter(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeFilter === t.id
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {/* Grouped summary */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-8 text-gray-500 text-sm">Loading subscriptions…</div>
        ) : groupNames.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-gray-200 rounded-lg text-gray-500 text-sm">
            No subscriptions configured.
            <br />
            Open an interface from the <strong>Logical Interfaces</strong> tab to add DHCP pools, NAT, or port forwards.
          </div>
        ) : (
          groupNames.map((ifaceName) => {
            const rows = groups[ifaceName];
            const collapsed = !!collapsedGroups[ifaceName];
            return (
              <div key={ifaceName} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => toggleGroup(ifaceName)}
                    className="flex items-center space-x-2 flex-1 text-left hover:text-primary-700"
                  >
                    {collapsed
                      ? <ChevronRight className="w-4 h-4 text-gray-500" />
                      : <ChevronDown className="w-4 h-4 text-gray-500" />}
                    <Network className="w-4 h-4 text-primary-600" />
                    <span className="text-sm font-semibold text-gray-800">{ifaceName}</span>
                    <span className="text-xs text-gray-500">
                      ({rows.length} subscription{rows.length === 1 ? '' : 's'})
                    </span>
                  </button>
                  {onNavigateToLogicalInterfaces && (
                    <button
                      type="button"
                      onClick={onNavigateToLogicalInterfaces}
                      className="inline-flex items-center space-x-1 text-xs font-medium text-primary-600 hover:text-primary-800"
                      title="Open this interface in the Logical Interfaces tab to edit its subscriptions"
                    >
                      <span>Edit in Logical Interfaces</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {!collapsed && (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white">
                      <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                        <th className="px-6 py-2">Interface</th>
                        <th className="px-6 py-2">Type</th>
                        <th className="px-6 py-2">Name</th>
                        <th className="px-6 py-2">Details</th>
                        <th className="px-6 py-2">Description</th>
                        <th className="px-6 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {rows.map((sub) => {
                        const subType = sub.subscription_type || sub.type;
                        const badge = getSubscriptionTypeBadge(subType);
                        const enabled = sub.enabled !== false && sub.enabled !== 0;
                        return (
                          <tr key={sub.id} className="hover:bg-gray-50">
                            <td className="px-6 py-3 whitespace-nowrap">
                              <span className="inline-flex items-center space-x-1.5 text-sm text-gray-700">
                                <Network className="w-3.5 h-3.5 text-primary-500" />
                                <span className="font-mono font-medium">{ifaceName}</span>
                              </span>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap">
                              <span className="inline-flex items-center space-x-1.5">
                                <TypeIcon subType={subType} />
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.color}`}>
                                  {badge.label}
                                </span>
                              </span>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {sub.subscription_name || sub.name || sub.id}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-xs text-gray-600 font-mono">
                              <SubscriptionDetails sub={sub} />
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                              {sub.description || '—'}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap">
                              <span
                                className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                  enabled
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {enabled ? 'Enabled' : 'Disabled'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const TypeIcon = ({ subType }: { subType: string }) => {
  if (subType === 'dhcp_pool') return <Server className="w-3.5 h-3.5 text-blue-600" />;
  if (subType === 'source_nat') return <Globe className="w-3.5 h-3.5 text-emerald-600" />;
  if (subType === 'port_forwarding') return <ArrowRightLeft className="w-3.5 h-3.5 text-purple-600" />;
  return null;
};

const SubscriptionDetails = ({ sub }: { sub: any }) => {
  const t = sub.subscription_type || sub.type;
  const c = sub.configuration || {};
  if (t === 'dhcp_pool') {
    return <span>{c.start_ip || '—'} → {c.end_ip || '—'}</span>;
  }
  if (t === 'source_nat') {
    const src = c.source_network || c.in_ip || '—';
    const dst = c.translated_address || c.out_ip || 'interface IP';
    return <span>{src} → {dst}</span>;
  }
  if (t === 'port_forwarding') {
    const proto = (c.protocol || 'TCP').toUpperCase();
    // VSNP-713: when the operator pinned this forward to a specific
    // external IP (alias picker in the modal), surface that IP so it's
    // distinguishable from a forward on a different external IP at the
    // same port. Empty external_ip means "use the parent's primary"
    // and we omit it to keep the common single-IP-WAN summary terse.
    const explicitExt = typeof c.external_ip === 'string' ? c.external_ip.trim() : '';
    const extPrefix = explicitExt ? `${explicitExt}:` : '';
    return <span>{proto} {extPrefix}{c.external_port} → {c.internal_ip}:{c.internal_port}</span>;
  }
  return <span>—</span>;
};

export default Subscriptions;
